import React, { useContext, useEffect, useState } from "react";
import {
  Input,
  Button,
  Header,
  Divider,
  Icon,
  Select,
  Pagination,
  Loading,
} from "../../components";
import { Box, Flex } from "@rebass/grid";
import {
  STable,
  STableHeader,
  STableColumn,
  STableRow,
} from "./styled/SDocuments";
import { Formik, Form } from "formik";
import * as Yup from "yup";
import { DocumentsContext } from "../../providers/DocumentsProvider";
import { GeoContext } from "../../providers/GeoProvider";
import { useTranslation } from "react-i18next";

interface SelectOption {
  label: string;
  value: string;
}

interface FormValues {
  [index: string]: any;
  companyname: string;
  casno: string;
  productname: string;
  privat1: string;
}

interface FilterItem {
  key: string;
  value: string;
}

interface SortOrder {
  label: string;
  order: string;
}

function createFilterItems(values: FormValues) {
  const items: FilterItem[] = [];
  Object.keys(values).forEach((key) => {
    let item: FilterItem = { key: "", value: "" };
    item.key = key;
    if (typeof values[key] === "object") {
      item.value = values[key].value;
    } else {
      item.value = values[key];
    }
    if (item.value !== "all") {
      items.push(item);
    }
  });
  return items;
}

function getPageCount(documentCount: number) {
  return Math.ceil(documentCount / 15);
}

function Documents() {
  const { t } = useTranslation();

  const DocumentSearchSchema = Yup.object().shape({
    companyname: Yup.string()
      .min(2, t("VALIDATION_MIN_LENGTH_PLACEHOLDER", { placeholder: 2 }))
      .max(50, t("VALIDATION_MAX_LENGTH_PLACEHOLDER", { placeholder: 50 })),
    casno: Yup.string()
      .min(2, t("VALIDATION_MIN_LENGTH_PLACEHOLDER", { placeholder: 2 }))
      .max(50, t("VALIDATION_MAX_LENGTH_PLACEHOLDER", { placeholder: 50 })),
    productname: Yup.string()
      .min(2, t("VALIDATION_MIN_LENGTH_PLACEHOLDER", { placeholder: 2 }))
      .max(50, t("VALIDATION_MAX_LENGTH_PLACEHOLDER", { placeholder: 50 })),
  });

  const {
    getDocuments,
    navigateDocuments,
    documents,
    documentsLoading,
    previousUrl,
    nextUrl,
    firstUrl,
    lastUrl,
    documentCount,
    currentPage,
  } = useContext(DocumentsContext);
  const {
    getLanguages,
    languages,
    languagesLoading,
    salesOrganizations,
    salesOrganizationsLoading,
    getSalesOrgs,
  } = useContext(GeoContext);
  const hasLanguagesInCache = Object.keys(languages).length !== 0;
  const [filterItems, setFilterItems] = useState<FilterItem[]>([]);

  const [sortOrder, setSortOrder] = useState<SortOrder>({
    label: "",
    order: "",
  });

  const [currentLanguage, setCurrentLanguage] = useState({
    label: t("ALL_LANGUAGES"),
    value: "all",
  } as SelectOption);
  const [currentSO, setCurrentSO] = useState({
    label: t("ALL_SALES_ORGS"),
    value: "all",
  } as SelectOption);

  useEffect(() => {
    getDocuments(filterItems, sortOrder);
    getSalesOrgs(true);
    if (!hasLanguagesInCache) {
      getLanguages(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getLanguageString = (langCode: string) => {
    const foundLanguage = languages.find((lang) => lang.value === langCode);
    return foundLanguage && foundLanguage.label;
  };

  const toggleOrder = (field: string) => {
    if (sortOrder.label === field) {
      sortOrder.order = sortOrder.order === "" ? "-" : "";
    } else {
      sortOrder.order = "";
      sortOrder.label = field;
    }
    setSortOrder(sortOrder);
    getDocuments(filterItems, sortOrder);
  };
  const resultContainer = () => {
    console.log(filterItems);

    if (documents !== undefined && documents.length === 0) {
      if (filterItems.length) {
        return (
          <Flex alignItems="center" justifyContent="center">
            <Box style={{ textAlign: "center" }}>
              <h3>{t("NO_RESULTS_FOUND")}</h3>
              <p>{t("TRY_DIFFERENT_SEARCH")}</p>
            </Box>
          </Flex>
        );
      } else {
        return (
          <Flex alignItems="center" justifyContent="center">
            <Box style={{ textAlign: "center" }}>
              <h3>{t("NO_ORDERS_FOUND")}</h3>
              <p>{t("CONTACT_SUPPORT")}</p>
            </Box>
          </Flex>
        );
      }
    }
    return <Table />;
  };

  const Table = () => {
    return (
      <STable id={"documents-table"}>
        <STableHeader>
          <tr>
            <th
              className={"material-name"}
              onClick={() => toggleOrder("companyname")}
            >
              <Flex alignItems="center" justifyContent="space-between">
                <span>{t("MATERIAL_NAME")}</span>
                <Flex>
                  <Icon name="caret" width={16} height={16} color="#000066" />
                  <Icon
                    width={16}
                    height={16}
                    name="caret"
                    color="#000066"
                    rotation={180}
                  />
                </Flex>
              </Flex>
            </th>
            <th
              className={"material-number"}
              onClick={() => toggleOrder("productname")}
            >
              <Flex alignItems="center" justifyContent="space-between">
                <span>{t("MATERIAL_NUMBER")}</span>
                <Flex>
                  <Icon name="caret" width={16} height={16} color="#000066" />
                  <Icon
                    width={16}
                    height={16}
                    name="caret"
                    color="#000066"
                    rotation={180}
                  />
                </Flex>
              </Flex>
            </th>
            <th className={"cas-number"}>{t("CAS_NUMBER")}</th>
            <th className={"sales-organisation"}>{t("SALES_ORG")}</th>
            <th className={"validity-area"}>{t("VALIDITY_AREA")}</th>
            <th className={"document-language"}>{t("LANGUAGE")}</th>
            <th className={"document-version"}>{t("VERSION")}</th>
            <th className={"user-actions"}>{t("ACTION")}</th>
          </tr>
        </STableHeader>
        <tbody>
          {documents.map((document, index) => {
            return (
              <STableRow key={index}>
                <STableColumn className={"material-name"}>
                  {document.companyname}
                </STableColumn>
                <STableColumn className={"material-number"}>
                  {document.productname}
                </STableColumn>
                <STableColumn className={"cas-number"}>
                  {document.casno}
                </STableColumn>
                <STableColumn className={"sales-organisation"}>
                  {document.privat3}
                </STableColumn>
                <STableColumn className={"validity-area"}>
                  {document.country}
                </STableColumn>
                <STableColumn className={"document-language"}>
                  {getLanguageString(document.privat1)}
                </STableColumn>
                <STableColumn className={"document-version"}>
                  {document.versionno}
                </STableColumn>
                <STableColumn className={"user-actions"}>
                  <Flex justifyContent="space-between" alignItems="center">
                    <Box>
                      <a
                        target="_blank"
                        rel="noopener noreferrer"
                        className={"user-actions-view"}
                        href={`${process.env.REACT_APP_API_ROOT}/documents/${document.document_id}/view`}
                      >
                        <Icon name="view" color="#000" />{" "}
                      </a>
                    </Box>
                    <Box>
                      <a
                        className={"user-actions-download"}
                        href={`${process.env.REACT_APP_API_ROOT}/documents/${document.document_id}/download`}
                      >
                        <Icon name="download" color="#000" />{" "}
                      </a>
                    </Box>
                  </Flex>
                </STableColumn>
              </STableRow>
            );
          })}
        </tbody>
      </STable>
    );
  };

  const updateSelect = (setFieldValue: Function, fieldName: string) => {
    function change(...args: any[]) {
      const selected = args[0] as SelectOption;
      fieldName === "privat1"
        ? setCurrentLanguage(selected)
        : setCurrentSO(selected);
      setFieldValue(fieldName, selected.value);
    }
    return change;
  };

  const handleOnBlur = (setFieldTouched: Function, fieldName: string) => {
    function update() {
      setFieldTouched(fieldName, true);
    }
    return update;
  };

  return (
    <React.Fragment>
      <Flex
        data-page-id="documents"
        flexWrap="wrap"
        px={30}
        style={{ minWidth: "1280px" }}
      >
        <Box
          id="box-filters"
          width={1 / 5}
          p={30}
          mr={30}
          bg="white"
          style={{
            minHeight: "400px",
            boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.05)",
          }}
        >
          <Header>{t("FILTER")}</Header>
          <Formik
            initialValues={{
              companyname: "",
              casno: "",
              productname: "",
              privat1: "",
            }}
            validationSchema={DocumentSearchSchema}
            onSubmit={(values: FormValues) => {
              setFilterItems(createFilterItems(values));
              getDocuments(createFilterItems(values), sortOrder);
            }}
            render={({
              touched,
              errors,
              setFieldValue,
              setFieldTouched,
              resetForm,
              values,
            }) => (
              <Box>
                <Form method="post">
                  <Box mt={30}>
                    <Input
                      touched={touched.companyname}
                      error={errors.companyname}
                      id="companyname"
                      name="companyname"
                      label={t("SEARCH_BY_PLACEHOLDER", {
                        placeholder: t("MATERIAL_NAME"),
                      })}
                      type="text"
                      placeholder={t("ENTER_PLACEHOLDER", {
                        placeholder: t("MATERIAL_NAME"),
                      })}
                      icon="search"
                      value={values.companyname}
                    />
                  </Box>
                  <Box mt={30}>
                    <Input
                      touched={touched.casno}
                      error={errors.casno}
                      id="casno"
                      name="casno"
                      label={t("SEARCH_BY_PLACEHOLDER", {
                        placeholder: t("CAS_NUMBER"),
                      })}
                      type="text"
                      placeholder={t("ENTER_PLACEHOLDER", {
                        placeholder: t("CAS_NUMBER"),
                      })}
                      icon="search"
                      value={values.casno}
                    />
                  </Box>
                  <Box mt={30}>
                    <Input
                      touched={touched.productname}
                      error={errors.productname}
                      id="productname"
                      name="productname"
                      label={t("SEARCH_BY_PLACEHOLDER", {
                        placeholder: t("MATERIAL_NUMBER"),
                      })}
                      type="text"
                      placeholder={t("ENTER_PLACEHOLDER", {
                        placeholder: t("MATERIAL_NUMBER"),
                      })}
                      icon="search"
                      value={values.productname}
                    />
                  </Box>
                  <Box mt={30}>
                    {languagesLoading ? (
                      <Loading></Loading>
                    ) : (
                      <Select
                        name="privat1"
                        defaultValue={
                          {
                            label: t("ALL_LANGUAGES"),
                            value: "all",
                          } as SelectOption
                        }
                        onChange={updateSelect(setFieldValue, "privat1")}
                        onBlur={handleOnBlur(setFieldTouched, "privat1")}
                        options={languages}
                        value={currentLanguage}
                        id="languagecode"
                        label={t("LANGUAGE")}
                        placeholder={t("ALL_LANGUAGES")}
                      />
                    )}
                  </Box>
                  <Box mt={30}>
                    {salesOrganizationsLoading ? (
                      <Loading></Loading>
                    ) : (
                      <Select
                        name="privat3"
                        defaultValue={
                          {
                            label: t("ALL_SALES_ORGS"),
                            value: "all",
                          } as SelectOption
                        }
                        onChange={updateSelect(setFieldValue, "privat3")}
                        onBlur={handleOnBlur(setFieldTouched, "privat3")}
                        options={salesOrganizations}
                        value={currentSO}
                        id="salesOrganization"
                        label={t("SALES_ORG")}
                        placeholder={t("ALL_SALES_ORGS")}
                      />
                    )}
                  </Box>
                  <Box mt={30}>
                    <Divider />
                  </Box>
                  <Box mt={30}>
                    <Box>
                      <Button
                        id="button-search"
                        fullWidth={true}
                        kind="primary"
                        type="submit"
                        loading={documentsLoading}
                      >
                        {t("SEARCH")}
                      </Button>
                    </Box>
                    <Box mt={15}>
                      <Button
                        id="button-clear"
                        fullWidth={true}
                        kind="secondary"
                        type="button"
                        onClick={() => {
                          setCurrentLanguage({
                            label: t("ALL_LANGUAGES"),
                            value: "all",
                          } as SelectOption);
                          setCurrentSO({
                            label: t("ALL_SALES_ORGS"),
                            value: "all",
                          } as SelectOption);
                          resetForm();
                          getDocuments([], { label: "", order: "" }, 1);
                          setFilterItems([]);
                          setSortOrder({ label: "", order: "" });
                        }}
                      >
                        {t("CLEAR_ALL")}{" "}
                      </Button>
                    </Box>
                  </Box>
                </Form>
              </Box>
            )}
          />
        </Box>
        <Box
          id="box-results"
          width={"calc(80% - 30px)"}
          p={15}
          pt={30}
          bg="white"
          style={{ boxShadow: "0 2px 4px 0 rgba(0, 0, 0, 0.05)" }}
        >
          {documentsLoading ? (
            <Flex
              justifyContent="center"
              style={{ height: "100%" }}
              alignItems="center"
            >
              <Loading width={32} height={32} />
            </Flex>
          ) : (
            <React.Fragment>
              <Header bordered={false}>{t("DOCUMENTS")}</Header>
              <React.Fragment>{resultContainer()}</React.Fragment>
            </React.Fragment>
          )}
        </Box>
      </Flex>
      {documents.length > 0 && documentCount > 15 && (
        <Flex px={30}>
          <Box width={1 / 5} p={30} mr={30}></Box>
          <Flex
            width={"calc(80% - 30px)"}
            p={15}
            pt={30}
            justifyContent="center"
          >
            <Pagination
              currentPage={currentPage}
              pages={getPageCount(documentCount)}
              firstUrl={firstUrl}
              lastUrl={lastUrl}
              previousUrl={previousUrl}
              nextUrl={nextUrl}
              onFirst={() => navigateDocuments(firstUrl, 1)}
              onLast={() =>
                navigateDocuments(lastUrl, getPageCount(documentCount))
              }
              onPrevious={() => navigateDocuments(previousUrl, currentPage - 1)}
              onNext={() => navigateDocuments(nextUrl, currentPage + 1)}
              onPage={navigateDocuments}
            />
          </Flex>
        </Flex>
      )}
    </React.Fragment>
  );
}

export default Documents;
